import { Stack, Tooltip, Typography } from '@mui/material';
import {
  SecurityAssignmentStatus,
  type InventoryResource,
} from '@repo/api-gw-sdk';
import { useState } from 'react';

import { OptionMenu } from '@/components/option-menu/option-menu';
import { ExcludeFromRansomwareDialog } from '@/components/security/excludeFromRansomwareDialog';
import { Link } from '@/components/shared/link';
import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';
import { SecurityStatuses } from '@/data/inventory/data';

interface SecurityAssignmentStatusFieldProps {
  entity: InventoryResource;
  onChange?: () => Promise<void>;
}

export const SecurityAssignmentStatusField = (
  props: SecurityAssignmentStatusFieldProps
) => {
  const { entity, onChange } = props;
  const [excludeDialogOpen, setExcludeDialogOpen] = useState(false);
  const dal = useDAL();
  const { isAuthorizedResource, isAuthorized } = useRoles();
  const { snackbar } = useWorkspace();

  const exclude = async () => {
    await dal.security.excludeResource(entity.id);
    await onChange?.();
    setExcludeDialogOpen(false);
  };

  const cancelExclusion = () => {
    void dal.security.cancelResourceExclusion(entity.id).then(() => {
      void onChange?.();
    });
    snackbar.showMessage(
      `Canceled exclusion. ${entity.resourceName} can now be monitored for ransomware.`
    );
  };

  const isExcluded =
    entity.securityAssignmentStatus ===
    SecurityAssignmentStatus.SECURITY_EXCLUDED;
  const isSupported =
    entity.securityAssignmentStatus !==
    SecurityAssignmentStatus.SECURITY_UNSUPPORTED;

  return (
    !!entity.securityAssignmentStatus && (
      <>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <Stack gap='8px' direction='row' alignItems='center'>
            <Typography>
              {SecurityStatuses[entity.securityAssignmentStatus]?.name}
            </Typography>
            {isSupported && (
              <OptionMenu
                options={[
                  {
                    id: 'update-cdc-backup',
                    text: isExcluded
                      ? 'Cancel exclusion'
                      : 'Exclude from ransomware detection',
                    menuItemProps: {
                      disabled: !isAuthorizedResource(
                        'update:exclude_resource',
                        entity
                      ),
                      onClick: () =>
                        isExcluded
                          ? cancelExclusion()
                          : setExcludeDialogOpen(true),
                    },
                  },
                ]}
              />
            )}
          </Stack>
          <Tooltip
            disableInteractive={false}
            title={
              <Typography
                variant='body2'
                component='span'
                className='text-inherit'
              >
                Eon checks assigned resources for ransomware indicators. You can
                assign resources by specifying their backup policies or
                properties.{' '}
                <Link
                  disabled={!isAuthorized('read:policies')}
                  className='font-bold underline'
                  href='/settings/ransomware-detection'
                >
                  Manage ransomware detection
                </Link>
              </Typography>
            }
          >
            <i className='material-symbols-help-outline-rounded w-[20px] h-[20px]' />
          </Tooltip>
        </Stack>
        <ExcludeFromRansomwareDialog
          isOpen={excludeDialogOpen}
          onCancel={() => setExcludeDialogOpen(false)}
          triggerExclusion={exclude}
          resourceName={entity.resourceName}
        />
      </>
    )
  );
};
